import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import usePreventRefresh from '../../../../../../../shared/utils/hooks/usePreventRefresh';
import safeLocalStorage from '../../../../../../../shared/utils/safeLocalStorage';

import { FadeIn, LoadingCircle, InfoIcon } from 'hosman-material';
import { S_CircleCheck } from 'hosman-material';

import RENTAL_ESTIMATIONS from '../../../../../../../shared/apiPublic/ressources/rentalEstimations';
import { updateStep } from '../../../../../../reducers/_Pages/Public/rentalFunnelSlice';

import { pricePerMeter } from '../../../../../../../shared/utils/helpers/hosmanVars';

const KIND_LABELS = {
  apartment: 'un appartement',
  house: 'une maison'
}

const Success = () => {
  const { estimationData } = useSelector((state) => state.rentalFunnel);
  const [resultData, setResultData] = useState();
  const dispatch = useDispatch();
  usePreventRefresh();

  const createRentalEstimation = async () => {
    const mergedObj = Object.assign(
      {},
      estimationData.placeData,
      estimationData.mainInfos,
      estimationData.extraInfos,
      estimationData.lead,
      { kind: estimationData.typology },
      { price_expectations: estimationData.priceExpectations ? parseInt(estimationData.priceExpectations.replace(/\s/g, '')) : null },
      { furnished: estimationData.furnished },
      { vacant: estimationData.vacant },
      { waiting_time_period: estimationData.waitingTimePeriod },
      { competition: estimationData.competition }
    )

    try {
      if (safeLocalStorage.getItem('hasEffectRun') || mergedObj.email === undefined) return;

      safeLocalStorage.setItem('hasEffectRun', 'true');
      const response = await RENTAL_ESTIMATIONS.createRentalEstimation(mergedObj)
      safeLocalStorage.removeItem('hasEffectRun');
      setResultData(response.data)
    } catch (e) {
      safeLocalStorage.removeItem('hasEffectRun');
      const errorMessage = e.response?.data?.error?.message?.error || 'Une erreur est survenue lors de l\'envoi de votre demande'
      toast.error(errorMessage, { id: 'rental-estimation-fail', duration: 3000 })
      dispatch(updateStep({ stepNumber: 1 }))
    }
  }

  useEffect(() => {
    const hasEffectRun = safeLocalStorage.getItem('hasEffectRun');

    // Make sure to only create the estimation once
    if (!hasEffectRun) createRentalEstimation();
  }, []);

  const kindLabel = resultData ? (KIND_LABELS[resultData.kind] || 'un bien') : null

  const property = { kind: estimationData.typology, ...estimationData.mainInfos }

  return (
    <div data-testid='Success'>
      <FadeIn>
        <div className='flex flex-col items-center gap-6 text-center mx-auto'>
          {!resultData ? (
            <>
              <LoadingCircle />

              <p className='h2 text-ih-indigo'>Envoi de votre demande en cours...</p>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={S_CircleCheck} className='text-ih-green text-5xl' />

              <p className='h2 text-ih-indigo'>Merci pour votre demande !</p>

              <p className='text-ih-indigo-60'>
                Votre demande d'estimation locative pour {kindLabel} à {resultData.city} a bien été prise en
                compte. Un conseiller Hosman vous recontactera prochainement pour échanger sur votre projet de
                mise en location.
              </p>

              {resultData.rent && (
                <div className='flex flex-col gap-8 md:border md:border-ih-purple-30 p-3 rounded-3xl text-center text-ih-indigo bg-white w-full' data-testid='EstimatedPrices'>
                  <div className='flex flex-col'>
                    <p className='text-4xl font-semibold text-ih-blue'>{resultData.rent.toLocaleString('fr-FR')} € <sup>(CC)</sup></p>
                    <p className='font-medium'>soit {pricePerMeter(resultData.rent, property)}</p>
                    <p className='text-ih-indigo-50 mt-1'>Estimation moyenne</p>
                  </div>

                  <div className='flex justify-center md:justify-around gap-5 md:mx-auto'>
                    <div className='flex flex-col whitespace-nowrap'>
                      <p className='text-xl font-semibold'>{resultData.lower_rent.toLocaleString('fr-FR')} € <sup>(CC)</sup></p>
                      <p className='font-medium'>soit {pricePerMeter(resultData.lower_rent, property)}</p>
                      <p className='text-ih-indigo-50 mt-1'>Estimation basse</p>
                    </div>

                    <div className='border-l w-min mx-auto'></div>

                    <div className='flex flex-col whitespace-nowrap'>
                      <p className='text-xl font-semibold'>{resultData.upper_rent.toLocaleString('fr-FR')} € <sup>(CC)</sup></p>
                      <p className='font-medium'>soit {pricePerMeter(resultData.upper_rent, property)}</p>
                      <p className='text-ih-indigo-50 mt-1'>Estimation haute</p>
                    </div>
                  </div>

                  <div className='flex flex-col items-center gap-3'>
                    <div className='flex items-center gap-2 px-3 py-2 rounded-xl bg-ih-purple-10 border border-ih-purple-50 w-auto mx-auto'>
                      <InfoIcon tooltip={<p className='w-60 md:w-80'>Cette estimation reflète le marché locatif et ne tient pas compte du plafonnement légal des loyers applicable dans certaines zones. Le loyer définitif sera confirmé par votre conseiller Hosman selon le secteur et le type de bail.</p>} bgColor='bg-ih-indigo-30' iconColor='text-ih-indigo' />
                      <p className='text-sm font-medium'>Hors plafonnement des loyers</p>
                    </div>

                    <div className='flex items-center gap-2 px-3 py-2 rounded-xl bg-ih-purple-10 border border-ih-purple-50 w-auto mx-auto'>
                      <InfoIcon tooltip={<p className='w-60 md:w-80'>Ce score indique le niveau de confiance que nous accordons à l'estimation, basé sur la qualité et la quantité des données disponibles pour ce bien (localisation, caractéristiques, comparables récents). Plus le score est élevé, plus l'estimation est considérée comme fiable.</p>} bgColor='bg-ih-indigo-30' iconColor='text-ih-indigo' />
                      <p className='text-sm font-medium'>Fiabilité de l'algorithme : {resultData.confidence}%</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </FadeIn>
    </div>
  )
}

export default Success;
